import React from "react";
import { fmtNaira } from "@/lib/format";
import { styleFor } from "@/lib/askThemes";

export function AskProgress({ total = 0, target, currency, light = true }) {
  const pct = Math.min(100, Math.round((total / target) * 100));
  return (
    <div data-testid="ask-progress" className="mt-4">
      <div className={`h-2.5 rounded-full overflow-hidden ${light ? "bg-white/25" : "bg-secondary"}`}>
        <div className={`h-full rounded-full ask-progress-fill ${light ? "bg-white" : "bg-accent"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`mt-1.5 flex justify-between text-xs font-semibold ${light ? "text-white/85" : "text-muted-foreground"}`}>
        <span data-testid="ask-progress-amount">{fmtNaira(total, currency)} confirmed</span>
        <span>of {fmtNaira(target, currency)}</span>
      </div>
    </div>
  );
}

export default function AskCard({ ask, compact = false, testId = "ask-card" }) {
  const st = styleFor(ask);
  const isLend = ask.kind === "lend";
  const amount = isLend ? ask.lend?.amount : ask.amount;
  return (
    <div data-testid={testId} className="relative overflow-hidden rounded-[1.75rem] p-5 text-white shadow-xl shadow-emerald-950/15 ask-pop" style={{ background: st.bg }}>
      <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
      <div className="pointer-events-none absolute -left-8 bottom-0 h-24 w-24 rounded-full bg-black/10 blur-2xl" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/75">
            {isLend ? "Lend Me" : ask.occasion || (ask.mode === "open" ? "Open Ask" : "Gift Me")}
          </p>
          <h3 className={`mt-1 font-display font-extrabold tracking-tight leading-tight ${compact ? "text-lg" : "text-2xl"}`}>
            {ask.title || (isLend ? "Could you lend me?" : st.title || "Gift Me")}
          </h3>
        </div>
        <span className={`ask-float shrink-0 select-none ${compact ? "text-3xl" : "text-5xl"}`} aria-hidden>{st.emoji}</span>
      </div>
      {!compact && ask.message && <p className="relative mt-3 text-sm leading-relaxed text-white/90 whitespace-pre-line">{ask.message}</p>}
      {amount ? (
        <div className="relative mt-4 inline-flex items-baseline gap-1.5 rounded-2xl bg-white/15 px-3 py-1.5 backdrop-blur-sm">
          <span className="text-xs font-semibold text-white/80">{isLend ? "Borrow" : "Ask"}</span>
          <span data-testid="ask-card-amount" className="font-display text-xl font-extrabold">{fmtNaira(amount, ask.currency)}</span>
        </div>
      ) : null}
      {isLend && ask.lend?.repay_date && (
        <p className="relative mt-2 text-xs font-semibold text-white/85">Repay by {new Date(ask.lend.repay_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
      )}
      {!isLend && ask.target && ask.show_progress !== false && ask.confirmed_total !== undefined && (
        <AskProgress total={ask.confirmed_total} target={ask.target} currency={ask.currency} />
      )}
    </div>
  );
}
